// Slice-10 admin role index.

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import type { components } from "../api/identity.gen";
import { useI18n } from "../i18n/I18nContext";

// CursorPage.items in the OpenAPI spec is typed as `Record<string, never>[]`
// (`type: object` with no properties); allOf-intersecting it with the
// per-page item type collapses to `never` in TS land. Slice 12 closeout
// fixes the OpenAPI schema to use a generic page envelope; for slice 10
// we cast at the consumption site.
type RoleDto = components["schemas"]["RoleDto"];

export function RolesScreen(): JSX.Element {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ["roles", "index"],
    queryFn: async () => {
      const response = await apiClient.GET("/identity/roles", {
        params: { query: { limit: 50 } },
      });
      if (!response.data) {
        throw new Error("roles_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div>
      <h1>{t("roles.heading")}</h1>
      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items.length === 0 && <p>{t("roles.empty")}</p>}
      {query.data && query.data.items.length > 0 && (
        <table className="rc-table">
          <thead>
            <tr>
              <th>{t("roles.column.name")}</th>
              <th>{t("roles.column.displayName")}</th>
              <th>{t("roles.column.permissions")}</th>
              <th>{t("roles.column.builtIn")}</th>
            </tr>
          </thead>
          <tbody>
            {(query.data.items as unknown as RoleDto[]).map((role) => (
              <tr key={role.role_id}>
                <td><code>{role.name}</code></td>
                <td>{role.display_name}</td>
                <td>{role.permissions.length}</td>
                <td>{role.is_built_in ? "✔︎" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
